import { FloatingPortal, UseFloatingReturn } from '@floating-ui/react';
import { MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import classNames from 'classnames';
import { debounce } from 'lodash';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { ProjectMetadata, ProjectMetadataMap } from 'const/ProjectMetadata';
import { intlGet } from 'utils/intlGet';

export const PROJECT_METADATA_PICKER_ITEM_CLASSNAME = 'project-metadata-picker-item';

export const menuRenderFn = (
  options: MenuOption[],
  floating: UseFloatingReturn,
) => (
  anchorElementRef: React.RefObject<HTMLElement>,
  { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }: {
    selectedIndex: number | null;
    selectOptionAndCleanUp: (option: MenuOption) => void;
    setHighlightedIndex: (index: number) => void;
  },
): React.ReactPortal | React.ReactNode | null => {
  const debouncedUpdate = useMemo(
    () => debounce(() => {
      floating.update();
    }),
    [],
  );

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        debouncedUpdate();
      });

      if (anchorElementRef.current) {
        resizeObserver.observe(anchorElementRef.current);
      }

      resizeObserver.observe(document.body);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      debouncedUpdate.cancel();
    };
  }, [anchorElementRef, debouncedUpdate]);

  useEffect(() => {
    if (anchorElementRef.current) {
      floating.refs.setReference(anchorElementRef.current);
      floating.update();
    }
  }, [anchorElementRef]);

  if (anchorElementRef.current === null || options.length === 0) {
    return null;
  }

  return (
    <FloatingPortal>
      <div
        ref={floating.refs.setFloating}
        className="typeahead-popover"
        style={floating.floatingStyles}
      >
        <div className="typeahead-popover-tooltip">
          {intlGet('Artboard.ProjectMetadataTokenSuggestionsPopover', 'Tooltip')}
        </div>
        <ul>
          {options.map((option: MenuOption, index) => (
            <li
              key={option.key}
              className={classNames(PROJECT_METADATA_PICKER_ITEM_CLASSNAME, { selected: selectedIndex === index })}
              ref={option.setRefElement}
              id={'typeahead-item-' + index.toString()}
              onMouseEnter={(): void => {
                setHighlightedIndex(index);
              }}
              onMouseDown={(event): void => {
                event.stopPropagation();
                event.preventDefault();
              }}
              onClick={(): void => {
                selectOptionAndCleanUp(option);
              }}>
              <span className='option-title'>{`{{${(ProjectMetadataMap[option.key] as ProjectMetadata).title}}}`}</span>
              <span className='option-description'>{` - ${(ProjectMetadataMap[option.key] as ProjectMetadata).description}`}</span>
            </li>
          ))}
        </ul>
      </div>
    </FloatingPortal>
  );
};

export { useTypeaheadFloating } from './useTypeaheadFloating';
